import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { Box, IconButton } from "@mui/material";
import { LoadingButton } from '@mui/lab';

import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { Utils } from "common/Utils";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import TableStyle from "components/Content/Table/TableStyle.module.css";
import TableAllData from "components/Content/CustomTable/TableAllData";
import ComplianceReport from "components/Content/Table/ComplianceReport";
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import LoaderAnimation from "pages/LoaderAnimation";
import AximTooltip from "components/AximTooltip";

export default function ComplianceReportTable({ isVisible }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [isLoading, setIsLoading] = useState(false);
    const [complianceReports, setComplianceReports] = useState([]);
    const complianceReportsRef = useRef(complianceReports);
    complianceReportsRef.current = complianceReports;

    const [isGeneratingComplianceReport, setIsGeneratingComplianceReport] = useState(false);
    
    const [isPolling, setIsPolling] = useState(null);
    const [timeoutId, setTimeoutId] = useState(null);

    useEffect(() => {
        if (isVisible && companyId) {
            setIsLoading(true);
            apiGet(apiUrls.getComplianceReports(companyId)).then(response => {
                if (response.data) {
                    setComplianceReports(response.data);
                }
                else {
                    error.setTitle("An Error Occurred");
                    error.setText("An Error occurred loading compliance reports.");
                    error.setDoShowError(true);
                }
            }).catch(response => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading compliance reports.");
                error.setDoShowError(true);
            }).finally(() => {
                setIsLoading(false);
            });
        }
            
        if (timeoutId) {
            setIsPolling(false);
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    },[isVisible, companyId])

    const columns = useMemo(() => [
        {
            id: 'expander',
            Header: "",
            Cell: ({ row }) => {
                return <>
                        {row.isExpanded ?
                            <KeyboardArrowDownOutlinedIcon onClick={() => toggleExpandedRow(row)} /> :
                            <KeyboardArrowUpOutlinedIcon onClick={() => toggleExpandedRow(row)} />
                        }
                        <AximTooltip title="Generate Compliance Report">
                            <span>
                                <IconButton
                                    onClick={() => generateComplianceReport(row.original.complianceId)}
                                    color="primary"
                                    disabled={isGeneratingComplianceReport || row.original.reportQueueId}
                                >
                                    <NoteAddOutlinedIcon />
                                </IconButton>
                            </span>
                        </AximTooltip>
                        {row.original.reportQueueId ?
                            row.original.statusId <= 3 ? 
                                <AximTooltip title="Download Compliance Report">
                                    <span>
                                        <LoadingButton
                                            onClick={() => downloadComplianceReport(row.original.reportQueueId)}
                                            color="primary"
                                            disabled={row.original.statusId < 3}
                                            loadingPosition="center"
                                            loading={row.original.statusId < 3}
                                        >
                                            <CloudDownloadOutlinedIcon />
                                        </LoadingButton>
                                    </span>
                                </AximTooltip>
                            :   
                                <AximTooltip title="Contact Align Admin for help">
                                    <span>
                                        <LoadingButton
                                            disabled={true}
                                        >
                                            <ErrorOutlineOutlinedIcon color="Error"/>
                                        </LoadingButton>
                                    </span>
                                </AximTooltip>
                        : null}
                    </>
            },
        },
        {
            Header: "HW Benefit Date",
            accessor: (cell) => Utils.formatDate(cell.hwbenefitDate),
            Cell: ({row}) => (
              <Box>
                {Utils.formatDate(row.original.hwbenefitDate)}
              </Box>
            ),
            sortType: "dateSort",
        },
        {
            Header: "User",
            accessor: (cell) => cell.updateName,
        },
        {
            Header: "Update Date",
            accessor: (cell) => Utils.formatDate(cell.updateDate),
            Cell: ({row}) => (
              <Box>
                {Utils.formatDate(row.original.updateDate)}
              </Box>
            ),
            sortType: "dateSort",
        },
    ]);
    
    const toggleExpandedRow = (row) => {
        row.toggleRowExpanded();
    }

    const generateComplianceReport = (complianceId) => {
        setIsGeneratingComplianceReport(true);
        apiGet(apiUrls.generateComplianceReport(complianceId)).then(response => {
            if (response.data) {
                var cr = updateComplianceReportItem(
                    complianceReportsRef.current,
                    (cr) => cr.findIndex(c => c.complianceId === complianceId),
                    (copy) => {
                        copy.reportQueueId = response.data;
                        copy.statusId = 1;
                    }
                );
            }
            else {
                error.setTitle("An Error Occurred");
                error.setText("Unable to generate report.");
                error.setDoShowError(true);
            }
        }).catch(response => {
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred generating report.");
            error.setDoShowError(true);
        }).finally(() => {
            setIsGeneratingComplianceReport(false);
        });
    }

    const updateComplianceReportItem = (reportsArray, selectFunction, updateFunction) => {
        let cr = [...reportsArray];
        let index = selectFunction(cr);

        if (index > -1) {
            let copy = {...cr[index]}

            if (copy) {
                updateFunction(copy);
                cr[index] = copy;

                setComplianceReports(cr);
            }
        }

        return cr;
    }

    const downloadComplianceReport = (reportQueueId) => {
        Utils.downloadFile(apiUrls.downloadFile(companyId, reportQueueId), "ComplianceReport.pdf", null,
            () => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred downloading the file.");
                error.setDoShowError(true);
            });
    }

    useEffect(() => {
        if (complianceReports.some(r => r.statusId < 3)) {
            setIsPolling(true);
        }
    },[complianceReports])

    useEffect(() => {
        if (isPolling && !timeoutId) {
            poll();
        }
    },[isPolling, timeoutId]);

    const poll = () => {
        apiGet(apiUrls.getComplianceReportStatus(companyId)).then(response => {
            if (response.data) {
                var cr = complianceReportsRef.current;

                for (var index in response.data) {
                    let id = response.data[index];

                    cr = updateComplianceReportItem(
                        cr,
                        (cr) => cr.findIndex(r => r.reportQueueId === id.reportQueueId),
                        (copy) => {
                            copy.statusId = id.reportQueueStatusId;
                        }
                    );
                }

                if (cr && cr.some(r => r.statusId < 3)) {
                    let id = setTimeout(poll, 30000);
                    setTimeoutId(id);
                }
                else {
                    setIsPolling(false);
                }
            }
        });
    }

    const renderRowSubComponent = useCallback(
        ({ row, rowProps, visibleColumns }) => (
            <ComplianceReportSubRowAsync
                row={row}
                rowProps={rowProps}
                visibleColumns={visibleColumns}
            />
        ), []
    );
    
    return (
        <Box>
            {complianceReports ?
                <TableAllData
                    className={TableStyle.TimeSheetsWrapper}
                    columns={columns}
                    data={complianceReports}
                    isLoading={isLoading}
                    renderRowSubComponent={renderRowSubComponent}
                />
            : null}
        </Box>
    )
}

function ComplianceReportSubRowAsync({ row, rowProps, visibleColumns }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [data, setData] = useState([]);
  
    useEffect(() => {
        apiGet(apiUrls.getComplianceReport(row.original.complianceId)).then(response => {
            setIsLoading(true);
            setData(response.data);
        }).catch(response => {
            setHasError(true);
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);
  
    return (
        isLoading ? <LoaderAnimation /> :
        hasError ? <Box>Unable to load compliance report</Box> : 
        <ComplianceReport reportData={data} />
    );
  }